<template>
    <!-- Page header -->
    <Section
      v-if="companyReady"
      :title="$t('cameras.overview.title', { company: companyStore.company.CompanyName })"
      :badge="flowStore.flows?.length ? $t('cameras.overview.streamCount', { n: flowStore.flows.length }) : undefined"
      class="page-gutter"
    >
        <div class="row row-cols-auto" style="gap: 25px; justify-content: center;">
          
          <div v-if="isLoading">
            <div class="loader" style="margin-top: 400px;"></div>
          </div>
          
          <!-- Render flows if flowstore is available and loading is complete -->
          <template v-if="!isLoading">
          
            <!-- Render flows if available -->
            <div class="col" v-for="flow in flowStore.flows" v-bind:key="flow.FlowId">

              <div class="health-card">
                <div class="health-head">
                  <h5 class="health-title">{{ flow.FlowName }}</h5>

                  <div class="pill-row">
                    <!-- Detections on/off -->
                    <span class="chip" :class="flowStatusDictionary[flow.FlowId] ? 'chip-ok' : 'chip-warn'">
                      <i class="bi" :class="flowStatusDictionary[flow.FlowId] ? 'bi-activity' : 'bi-slash-circle'"></i>
                      {{ flowStatusDictionary[flow.FlowId] ? $t('cameras.detectionsOn') : $t('cameras.detectionsOff') }}
                    </span>

                    <!-- Detection Stream status -->
                    <span class="chip" :class="streamChips[flow.FlowId]?.toneClass" :title="streamChips[flow.FlowId]?.tooltip || ''">
                      <i class="bi" :class="streamChips[flow.FlowId]?.icon"></i>
                      {{ $t('cameras.overview.streamChip', { status: streamChips[flow.FlowId]?.label }) }}<template v-if="streamChips[flow.FlowId]?.durationText"> · {{ streamChips[flow.FlowId].durationText }}</template>
                    </span>

                    <!-- People currently in any zone — tooltip removed
                         per user feedback (label was confusing). The
                         chip stays as a pure indicator. -->
                    <span class="chip" :class="peopleChipClass(flow.FlowId)">
                      <i class="bi bi-people"></i>
                      {{ $t('cameras.overview.inZones', { n: flowStatusDictionary[flow.FlowId] ? totalsInZones[flow.FlowId] : '—' }) }}
                    </span>
                  </div>
                </div>

                <div class="health-actions">
                  <router-link
                    :to="`/flow/${flow.FlowId}`"
                    class="stora-btn ghost"
                    :aria-label="$t('cameras.overview.openLive')"
                  >
                    <i class="bi bi-display"></i>
                    <span>{{ $t('cameras.overview.live') }}</span>
                  </router-link>

                  <router-link
                    v-if="!isSmallScreen && userStore.currentCompany.role !== 'User'"
                    :to="`/flow_settings/${flow.FlowId}`"
                    class="stora-btn"
                    :aria-label="$t('cameras.overview.openSettings')"
                  >
                    <i class="bi bi-gear"></i>
                    <span>{{ $t('cameras.overview.settings') }}</span>
                  </router-link>
                </div>
              </div>

            </div>
              
            <!-- Display dummy card if no flows are available as an Admin and Key User-->
            <!-- Empty state (Admin/Key User) -->
            <div v-if="!flowStore.flows.length && !isLoading && userStore.currentCompany.role !== 'User'" class="col">
              <div class="health-card empty-card">
                <div class="empty-icon"><i class="bi bi-camera-video-off"></i></div>
                <h4 class="empty-title">{{ $t('cameras.overview.emptyTitle') }}</h4>
                <p class="empty-sub">{{ $t('cameras.overview.emptySub') }}</p>

                <router-link v-if="!isSmallScreen" to="/create_flow" class="stora-btn">
                  <i class="bi bi-plus-lg"></i><span>{{ $t('cameras.overview.createFirstFlow') }}</span>
                </router-link>
                <p v-else class="empty-hint">{{ $t('cameras.useDesktop') }}</p>
              </div>
            </div>

            <!-- Display dummy card if no flows are available as a User-->
            <div v-if="!flowStore.flows.length && !isLoading && userStore.currentCompany.role === 'User'" class="col">
              <div class="health-card empty-card">
                <div class="empty-icon"><i class="bi bi-camera-video-off"></i></div>
                <h4 class="empty-title">{{ $t('cameras.overview.userEmptyTitle') }}</h4>
                <p class="empty-sub">{{ $t('cameras.overview.userEmptySub') }}</p>
              </div>
            </div>

          </template>

        </div>
    </Section>
</template>

<script>
import axios from 'axios'

import { onBeforeMount, onBeforeUnmount, computed, ref } from 'vue'
import { RouterLink } from 'vue-router'

import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'

import { detection_zone_colors_by_name_solid } from '@/constants.js'
import { useFlowStore } from '@/stores/FlowStore'
import { useCameraStreamStore } from '@/stores/CameraStreamStore'
import { parseDeviceUrl, liveDeviceUrl, isLiveRelayUrl, liveProbeUrl } from '@/api/deviceUrl'
import { useUserStore } from '@/stores/UserStore'
import { useCompanyStore } from '@/stores/CompanyStore'

import Section from '@/components/ui/Section.vue'
import Tooltip from '@/components/ui/Tooltip.vue'

import io from 'socket.io-client'

import { deriveStreamChip } from '@/composables/streamStatus'

export default {
  components: {
    Section,
    Tooltip,
    RouterLink,
  },


  setup() {
    const socket = ref(null)
    const isConnectingSocket = ref(false)
    const socketConnected = ref(true)

    const toast = useToast()
    const { t } = useI18n()
    
    const flowStore = useFlowStore()
    const CameraStreamStore = useCameraStreamStore()
    const userStore = useUserStore()
    const companyStore = useCompanyStore()

    const StreamLink = ref('')

    const flowStatusDictionary = ref({})

    const cameraHeartBeat = ref({})
    const heartbeatTimeouts = ref({})

    // Latest rich `stream_status` per flowId (new supervisor-equipped devices only).
    // Falls back to the heartbeat watchdog when absent/stale — see deriveStreamChip.
    const streamRichStatus = ref({})
    
    const detectionZoneNames = ref({})
    const flowZoneCounters = ref({})
    const streamToFlowMap = ref({})

    const isSmallScreen = ref(window.innerWidth <= 768)

    const isLoading = ref(false)
    const isLoadingSnap = ref({})

    const socketioURL = computed(() => {
        return {
          // Live events come from the box (tunnel) or, for a store on LiveTransport 'iothub',
          // from the portal's live relay; same events either way. See @/api/deviceUrl.
          URLDevice: liveDeviceUrl(companyStore.company)
        }
    })

    const detectNetworkContext = async (deviceURL) => {
        try {
            const controller = new AbortController()
            const signal = controller.signal
            const timeout = 5000 // 5 seconds timeout for checking public IP

            console.log('Trying device URL:', deviceURL)
            // The box answers on its root (tunnel); the live relay on /status.
            const probeUrl = isLiveRelayUrl(deviceURL) ? liveProbeUrl(deviceURL) : parseDeviceUrl(deviceURL).httpBase
            const fetchPromise = fetch(probeUrl, { signal })
            setTimeout(() => controller.abort(), timeout)

            const response = await fetchPromise
            if (response.ok) {
                console.log('Device URL is accessible')
                return deviceURL
            }
        } catch (error) {
            console.error('Device Socket URL is not accessible')
            toast.error(t('cameras.deviceNotAccessible'))
            throw new Error('Device Socket URL is not accessible')
        }
        throw new Error('Device Socket URL is not accessible')
    }

    const establishSocketConnection = async (deviceURL) => {
        if (socket.value !== null) {
            console.log("Returning existing socket")
            return socket.value
        }

        if (isConnectingSocket.value) {
            console.log('Already connecting, waiting...')
            while (isConnectingSocket.value) {
                await new Promise(r => setTimeout(r, 500))
            }
            return socket.value
        }

        console.log("Connecting socket ...")
        isConnectingSocket.value = true

        try {
            const url = await detectNetworkContext(deviceURL)
            console.log('Network context detected, URL:', url)

            // io(url) would read "/device/<id>" as a NAMESPACE, not a path prefix — it
            // would ask for a namespace the edge does not define, over a socket.io path
            // nginx does not route. Origin and path must be passed separately.
            // For the legacy ":500x" shape these are the whole URL and "/socket.io",
            // i.e. exactly what io(url) resolved to on its own. See @/api/deviceUrl.
            const { origin, socketPath, proxied } = parseDeviceUrl(url)

            socket.value = io(origin, {
                path: socketPath,
                // Same-origin once this store is cut over, so this is a no-op that
                // records the intent. NOT unconditional: on the legacy cross-origin
                // shape the edge replies "Access-Control-Allow-Origin: *", which a
                // browser REFUSES for a credentialed request — it would break the
                // live view of every store not yet cut over.
                withCredentials: proxied,
                transports: ['websocket'],
                reconnection: true,
                // Infinity, not 5: after 5 attempts (~5s) socket.io gives up permanently and only a
                // page reload recovers. A tunnel drop legitimately lasts up to ~60s while sshd frees
                // the device's 500x port. See NavSidebar.vue for the full note.
                reconnectionAttempts: Infinity,
                reconnectionDelay: 1000,
                reconnectionDelayMax: 10000,
                debug: true,
                perMessageDeflate: false,
                timeout: 120000,
                maxPayload: 50 * 1024 * 1024,
            })

            setupSocketEvents(socket.value)

            return socket.value

        } catch (error) {
            console.error('Error establishing socket connection:', error)
            throw error
        } finally {
            console.log('Resetting serverConnection flag')
            isConnectingSocket.value = false
        }
    }

    const setupSocketEvents = (socketInstance) => {
      socketInstance.on('heartbeat', (data) => {
          const { streamId, heartbeat } = data

          // Ensure we are working with the raw data from the proxy
          const cameraStreams = CameraStreamStore.inputcamerastreams.slice()

          // Perform explicit type conversion for comparison
          const cameraStream = cameraStreams.find(item => item.CameraStreamId === Number(streamId))
          
          if (cameraStream) {
            const flowId = cameraStream.FlowId
            
            if (heartbeat && flowId) {
              
              cameraHeartBeat.value[flowId] = true
              clearTimeout(heartbeatTimeouts.value[flowId])
              heartbeatTimeouts.value[flowId] = setTimeout(() => {
                cameraHeartBeat.value[flowId] = false
              }, 5000)
            }
          }
        })

        // Rich health from the new edge stream-supervisor (new devices only).
        socketInstance.on('stream_status', (data) => {
          const { streamId } = data
          const cameraStream = CameraStreamStore.inputcamerastreams
            .find(item => item.CameraStreamId === Number(streamId))
          if (cameraStream) {
            streamRichStatus.value[cameraStream.FlowId] = { ...data, _at: Date.now() }
          }
        })

        socketInstance.on('zone_counter', (data) => {
          const streamId = data.stream_id
          const flowId = streamToFlowMap.value[streamId]

          if (flowId) {
              if (!flowZoneCounters.value[flowId]) {
                  // Ensure a default structure if the flowId entry is missing
                  flowZoneCounters.value[flowId] = {}
              }

              // Update with data or leave defaults as 0
              flowZoneCounters.value[flowId] = {
                  ...flowZoneCounters.value[flowId], // Keep existing structure
                  ...data.zone_counter // Overwrite with new data
              }
          }
        })

        socketInstance.on('disconnection_message', (data) => {
                console.log('message on disconnection: ', data)
            })

        socketInstance.on('error', (error) => {
          console.error('Error received from server:', error)     
          toast.error(t('cameras.error', { message: error.message }))
        })
      }

    
    const initializeSocketConnection = async () => {
        if (socketioURL.value.URLDevice) {
          try {
            socket.value = await establishSocketConnection(socketioURL.value.URLDevice)
            console.log('Socket connection Home established')
          } catch (error) {
            console.error('Failed to establish Home socket connection:', error)
            socketConnected.value = false
          }
        } else {
          console.error('socketioURL is not defined')
          socketConnected.value = false
        }
    }

    const companyReady = computed(() => !!companyStore.company?.CompanyName)

    const handleResize = () => {
      isSmallScreen.value = window.innerWidth <= 768
    }


    onBeforeMount(async () => {
        isLoading.value = true
        // console.log('Starting data fetch...')
        
        try {
          await userStore.fetchUserDetails()
          // console.log('User details fetched')
          await flowStore.fetchFlows()
          // console.log('Flows fetched')
          await CameraStreamStore.fetchInputCameraStreams()
          // console.log('Input camera streams fetched')
          await fetchAllFlowDetails()
          // console.log('All flow details fetched')
          
          await initializeSocketConnection()

          initializeFlowData()

          window.addEventListener('resize', handleResize)
          handleResize() // Set initial state based on current window size
        
        } catch (error) {
          console.error('Error during onBeforeMount:', error)
        
        } finally {
          isLoading.value = false
        }
    })

    const fetchAllFlowDetails = async () => {
        const fetchFlowDetailsPromises = flowStore.flows.map(flow => flowStore.fetchCurrentFlow(flow.FlowId))
        const flowDetailsArray = await Promise.all(fetchFlowDetailsPromises)
        flowDetailsArray.forEach((flowDetails, index) => {
          const flowId = flowStore.flows[index].FlowId
          if (flowDetails && flowDetails.DetectionZones) {
            detectionZoneNames.value[flowId] = flowDetails.DetectionZones
            flowZoneCounters.value[flowId] = Object.keys(flowDetails.DetectionZones).reduce((acc, zone) => {
              acc[zone] = 0 // Initialize with 0 or any default value
              return acc
            }, {})
          } else {
            console.warn(`Flow ${flowId} does not have DetectionZone data`)
            detectionZoneNames.value[flowId] = {}
            flowZoneCounters.value[flowId] = {}
          }
        })
    }

    const initializeFlowData = () => {
        flowStatusDictionary.value = CameraStreamStore.inputcamerastreams.reduce((acc, item) => {
          acc[item.FlowId] = item.StreamStatus
          return acc
        }, {})

        StreamLink.value = CameraStreamStore.inputcamerastreams.reduce((acc, item) => {
          acc[item.FlowId] = item.CameraStreamLink
          return acc
        }, {})

        cameraHeartBeat.value = CameraStreamStore.inputcamerastreams.reduce((acc, item) => {
          acc[item.FlowId] = false // Initialize as false
          return acc
        }, {})

        streamToFlowMap.value = CameraStreamStore.inputcamerastreams.reduce((acc, item) => {
          acc[item.CameraStreamId] = item.FlowId
          return acc
        }, {})
    }

    // helper to sum all non-cash zones
    const totalsInZones = computed(() => {
      const out = {}
      for (const flow of flowStore.flows) {
        const id = flow.FlowId
        const counters = flowZoneCounters.value?.[id] || {}
        const meta = detectionZoneNames.value?.[id] || {}
        let sum = 0
        for (const [zone, count] of Object.entries(counters)) {
          if (meta[zone]?.type !== 'Cash Register Zone') sum += Number(count) || 0
        }
        out[id] = sum
      }
      return out
    })

    const peopleChipClass = (flowId) => {
      // detections off OR unhealthy stream → muted
      if (!flowStatusDictionary.value[flowId]) return 'chip-muted'
      if (!cameraHeartBeat.value[flowId])     return 'chip-muted'

      // healthy & on → green if >0, idle if 0
      const total = totalsInZones.value[flowId] || 0   // <<< use .value
      return total > 0 ? 'chip-ok' : 'chip-idle'
    }

    // Stream-health chip: rich supervisor status when present, heartbeat watchdog otherwise.
    const streamChip = (flowId) => {
      const rich = streamRichStatus.value[flowId]
      return deriveStreamChip({
        dbStreamStatus: flowStatusDictionary.value[flowId],
        richStatus: rich || null,
        richStatusAt: rich?._at ?? null,
        heartbeatAlive: !!cameraHeartBeat.value[flowId],
      })
    }
    // Precomputed per-flow so the template binds one object instead of recomputing per field.
    const streamChips = computed(() => {
      const out = {}
      for (const flow of flowStore.flows) out[flow.FlowId] = streamChip(flow.FlowId)
      return out
    })

    const getColorForZone = (zoneName, flowId) => {
        const zoneType = detectionZoneNames.value[flowId]?.[zoneName]?.type
        return detection_zone_colors_by_name_solid[zoneType] || 'black'
    }

    const onCancel = () => {
        console.log('User cancelled the loader.')
    }

    onBeforeUnmount(() => {
        if (socket.value) {
          socket.value.off('zone_counter')
          socket.value.off('heartbeat')
          socket.value.off('stream_status')
          socket.value.off('error')
          socket.value.off('connect')
          socket.value.disconnect()
        }
        Object.values(heartbeatTimeouts.value).forEach(clearTimeout)
        // Remove the resize event listener when component is unmounted
        window.removeEventListener('resize', handleResize)
    })

    return { 
        companyReady,  
        socketConnected,
        companyStore,
        flowStore,
        userStore,
        flowStatusDictionary,
        cameraHeartBeat,
        streamChips,
        flowZoneCounters,
        detectionZoneNames,
        detectionZoneColorsSolid: detection_zone_colors_by_name_solid,
        totalsInZones,
        peopleChipClass,
        getColorForZone,
        isLoading,
        isLoadingSnap,
        isSmallScreen,
        onCancel
    }
  }
}
</script>

<style scoped>
.card-title {
  color: #4F7EB3;
}

.card {
  width: 27rem;
  margin-bottom: 20px;
}

.dummy-card {
  border-color: #ddd; /* Light gray border */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); /* Optional: Add shadow for raised card effect */
  color: #666; /* Gray text color */
}

.cards-container {
    margin-top: 20px;
    display: flex;
    flex-wrap: wrap;
    flex: 1;
    box-sizing: border-box; /* Ensure padding/margin do not affect the size */
}

.counter-card {
    flex: 1 1 100%; /* Fixed width for each counter card */
    box-sizing: border-box;
    min-width: 150px;
}

.counter-card.greyed-out {
    background-color: #f0f0f0; /* Light grey background */
    color: #a0a0a0; /* Grey text color */
}

.counter-card-header {
    padding: 10px;
    text-align: left;
}

.counter-card-title {
    margin: 0; /* Remove default margin */
    font-size: small;
    color: #4F7EB3 !important
}

.count {
    display: block;
    padding: 10px;
}

.zone-table {
  display: flex;
  flex-direction: column;
}

.zone-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
}

.zone-name {
  flex: 1;
  font-size: small;
}

.zone-count {
  flex: 0 0 50px; /* Fixed width to align counts */
  text-align: right;
  font-size: small;
}

.error-text {
  color: red;
  font-size: small;
  text-align: center;
  margin-top: 10px;
}

.page-head{
  display:flex; align-items:center; justify-content:space-between;
  gap:12px; margin:0 0 16px 0;
}
.page-title{
  margin:0; font-size:1.25rem; color:#4F7EB3; display:flex; align-items:center; gap:8px;
}
.page-actions{ display:flex; align-items:center; gap:8px; }

.row {padding-top: 40px;}
.row.row-cols-auto{ gap:22px; justify-content:start; }
.health-card{
  width: 26rem;
  background:#fff; border:1px solid #e6edf3; border-radius:12px;
  box-shadow:0 3px 12px rgba(13,38,76,.08);
  padding:14px 14px 10px; display:flex; flex-direction:column; gap:12px;
}

/* Header */
.health-head{ display:flex; flex-direction:column; gap:10px; }
.health-title{ margin:0; font-size:1rem; color:#4F7EB3; }

/* Pills row */
.pill-row{ display:flex; flex-wrap:wrap; gap:8px; }

/* Chips (reused) */
.chip{
  display:inline-flex; align-items:center; gap:6px;
  border-radius:999px; padding:4px 10px; font-size:.78rem;
  border:1px solid #e6edf3; color:#4F7EB3; background:#f7fbff;
}
.chip-ok{    color:#2c7a3f; background:#f3fbf5; border-color:#d9f1df; }
.chip-warn{  color:#b66a00; background:#fff7e6; border-color:#ffe7bf; }
.chip-muted{ color:#81909a; background:#f3f5f7; border-color:#e9eef3; } /* neutral */
.chip-idle{  color:#b66a00; background:#eef3f7; border-color:#dfe8ef; } /* 0 visitors */

/* Actions */
.health-actions{
  display:flex; gap:8px; justify-content:flex-end; align-items:center;
  border-top:1px dashed #e6edf3; padding-top:10px;
}

/* Buttons (brand-consistent) */
.stora-btn{
  display:inline-flex; align-items:center; gap:8px;
  padding:8px 12px; border-radius:8px; border:1px solid transparent;
  background:#4F7EB3; color:#fff; text-decoration:none; font-size:.86rem;
  transition: background .15s ease, border-color .15s ease, color .15s ease;
}
.stora-btn:hover{ background:#B2C149; }
.stora-btn.ghost{
  background:#fff; color:#4F7EB3; border-color:#e6edf3;
}
.stora-btn.ghost:hover{
  background:#f4f7fb; border-color:#cfe0f3; color:#4F7EB3;
}

.empty-card{ align-items:center; text-align:center; gap:10px; padding:28px 18px; }
.empty-icon{ font-size:36px; color:#9aa8b3; }
.empty-title{ margin:6px 0 0 0; color:#4F7EB3; font-size:1.1rem; }
.empty-sub{ margin:0; color:#6c7a86; font-size:.9rem; }
.empty-hint{ margin:8px 0 0 0; color:#6c7a86; font-size:.86rem; }


.loader {
  width: 80px;
  padding: 8px;
  aspect-ratio: 1;
  border-radius: 50%;
  background: #B2C149;
  --_m: 
    conic-gradient(#0000 10%,#000),
    linear-gradient(#000 0 0) content-box;
  -webkit-mask: var(--_m);
          mask: var(--_m);
  -webkit-mask-composite: source-out;
          mask-composite: subtract;
  animation: spinner 1s infinite linear;
}
@keyframes spinner {to{transform: rotate(1turn)}}

.placeholder-animation {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.loading-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(to left, transparent 0%, #E0E0E0 50%, transparent 100%);
  background-size: 200% 100%;
  animation: loadingplaceholder 1.5s ease-in-out infinite;
}

@keyframes loadingplaceholder {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 768px) {
    /* 100% of the content column — not 100vw, which ignores the sidebar offset
       and the scrollbar width and would overflow sideways. */
    .health-card{ width: 100%; }
}


</style>